// =============================================================================
// ServiceMailsTab — de transactionele mails zelf schrijven
// =============================================================================
// De mails die bij een boeking horen: de bevestiging met het ticket, de
// wijzigingsbevestiging, de herinnering op de dag zelf, de link om te
// verzetten, de betaallink en de twee bonnen. Tot nu toe stonden die vast in
// de code; hier kun je per mail een eigen versie schrijven, in dezelfde editor
// als de campagnes en de opvolgmails (per taal, blokken of geplakte HTML).
//
// Bovenop de gewone tokens heeft elke mail zijn eigen:
//   * waarden, zoals {{boekingsnummer}}, {{datum}} of {{code}};
//   * onderdelen, zoals {{qr_code}}, {{boekingsdetails}} of {{betaalbanner}}:
//     die worden een kant-en-klaar stuk mail, precies zoals in de standaardmail.
//     Een onderdeel dat niet van toepassing is (geen openstaand bedrag, één
//     tijdslot) blijft leeg, en een blok met alleen dat token valt dan weg;
//   * voorwaarden, zoals {{#onbetaald}} … {{/onbetaald}}: wat ertussen staat
//     komt alleen in de mail als het van toepassing is ({{^…}}: als het niet
//     zo is). Zo bouw je de betaalbanner in je eigen stijl na.
//
// Zonder {{unsubscribe_url}} in de mail komt de standaard-voettekst eronder:
// waarom de klant de mail krijgt, en afmelden voor nieuws en aanbiedingen.
//
// Zolang een mail geen eigen versie heeft, of die uit staat, gaat de
// standaardmail. Die gaat ook als een eigen versie onverhoopt niet te maken
// is: een template kan het uiterlijk van het ticket veranderen, nooit of het
// aankomt. Niets dwingt het QR-ticket af — de editor waarschuwt alleen.
import { useEffect, useRef, useState } from 'react';
import { formatVenueDateTime } from '../lib/time';
import type { BookingLocation } from '../lib/walkin-data';
import {
  listEmailTemplates,
  previewEmailTemplate,
  resetEmailTemplate,
  saveEmailTemplate,
  testSendEmailTemplate,
  type EmailTemplatesError,
  type SampleScenario,
  type TransactionalKind,
  type TransactionalMail,
} from '../api/emailTemplates';
import type { CampaignContent, CampaignLocale, CampaignVariant } from '../api/marketing';
import { BlocksEditor, ScaledEmailFrame } from './MarketingScreen';

const ERROR_LABEL: Record<string, string> = {
  permission_denied: 'Je hebt geen rechten voor de servicemails.',
  invalid_content: 'De inhoud is niet compleet.',
  invalid_to: 'Vul een geldig e-mailadres in voor de testmail.',
  render_failed: 'Deze mail kon niet gemaakt worden.',
};

function errText(e: EmailTemplatesError | null): string {
  if (!e) return 'Onbekende fout';
  const base = ERROR_LABEL[e.error] ?? e.error;
  return e.message ? `${base} (${e.message})` : base;
}

function emptyVariant(): CampaignVariant {
  return {
    subject: '',
    preheader: null,
    mode: 'blocks',
    blocks: [{ type: 'heading', text: 'Hoi {{voornaam}},' }, { type: 'paragraph', text: '' }],
  };
}

// The mails where an unpaid booking, a split group, a 0% VAT venue or a
// reduction instead of a cancellation changes what is shown.
const BOOKING_KINDS: TransactionalKind[] = ['confirmation', 'rescheduled'];
const SPLIT_KINDS: TransactionalKind[] = ['confirmation', 'rescheduled', 'arrival_reminder'];
const REFUND_KINDS: TransactionalKind[] = ['refund_voucher'];

// Every text a variant carries, to look for tokens in.
function variantText(v: CampaignVariant): string {
  const blocks = (v.blocks ?? [])
    .map((b) => ('text' in b ? b.text : `${b.url} ${'label' in b ? b.label : ''}`))
    .join(' ');
  return `${v.subject} ${v.mode === 'html' ? v.html ?? '' : blocks}`;
}

// Same forgiving match the server uses: case, spaces, underscores, hyphens.
function hasToken(text: string, token: string): boolean {
  const norm = (s: string) => s.trim().toLowerCase().replace(/[\s_-]+/g, ' ');
  const want = norm(token);
  for (const m of text.matchAll(/\{\{\s*([^{}]{1,64}?)\s*\}\}/g)) {
    if (norm(m[1] ?? '') === want) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
export function ServiceMailsTab({ locations }: { locations: BookingLocation[] }) {
  const [mails, setMails] = useState<TransactionalMail[]>([]);
  const [fromAddress, setFromAddress] = useState('');
  const [editing, setEditing] = useState<TransactionalKind | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editing !== null) return;
    let cancelled = false;
    setLoading(true);
    void listEmailTemplates().then((res) => {
      if (cancelled) return;
      if (res.ok && res.data) {
        setMails(res.data.mails);
        setFromAddress(res.data.from_address);
        setError(null);
      } else setError(errText(res.error));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [editing]);

  if (editing) {
    const mail = mails.find((m) => m.kind === editing);
    if (mail) {
      return (
        <ServiceMailEditor mail={mail} locations={locations} onClose={() => setEditing(null)} />
      );
    }
  }

  return (
    <div className="marketing-followup">
      {error ? <div className="error-card">{error}</div> : null}
      <section className="marketing-panel">
        <h2>Servicemails</h2>
        <p className="muted marketing-hint">
          De mails die bij een boeking horen. Zonder eigen versie, of als die uit staat, gaat
          de standaardmail{fromAddress ? <> vanaf <strong>{fromAddress}</strong></> : null}.
          Ook als een eigen versie onverhoopt niet gemaakt kan worden gaat de standaardmail, dus
          een klant krijgt altijd zijn ticket.
        </p>
        {loading ? <p className="muted">Laden…</p> : (
          <table className="vouchers-table marketing-table">
            <thead>
              <tr>
                <th>Mail</th>
                <th>Wanneer</th>
                <th>Status</th>
                <th>Gewijzigd</th>
              </tr>
            </thead>
            <tbody>
              {mails.map((m) => (
                <tr key={m.kind} className="marketing-row" onClick={() => setEditing(m.kind)}>
                  <td><strong>{m.label}</strong></td>
                  <td className="muted">{m.when}</td>
                  <td>
                    {m.template?.enabled ? (
                      <span className="pill marketing-pill-sent">Eigen versie</span>
                    ) : m.template ? (
                      <span className="pill marketing-pill-draft">Eigen versie (uit)</span>
                    ) : (
                      <span className="pill marketing-pill-draft">Standaard</span>
                    )}
                  </td>
                  <td className="muted">
                    {m.template ? formatVenueDateTime(m.template.updated_at) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor voor één mail
// ---------------------------------------------------------------------------
function ServiceMailEditor({
  mail,
  locations,
  onClose,
}: {
  mail: TransactionalMail;
  locations: BookingLocation[];
  onClose: () => void;
}) {
  const saved = mail.template;
  // No saved version yet: start from the text of the standard mail, so the
  // first edit is a tweak rather than a blank page.
  const [content, setContent] = useState<CampaignContent>(
    saved && Object.keys(saved.content ?? {}).length > 0 ? saved.content : mail.defaults,
  );
  const [defaultLocale, setDefaultLocale] = useState<CampaignLocale>(saved?.default_locale ?? 'nl');
  const [enabled, setEnabled] = useState(saved?.enabled ?? false);
  const [exists, setExists] = useState(saved != null);
  const [lang, setLang] = useState<CampaignLocale>(saved?.default_locale ?? 'nl');
  const [showBuiltin, setShowBuiltin] = useState(false);
  const [scenario, setScenario] = useState<SampleScenario>({
    unpaid: false,
    split: false,
    no_vat: false,
    reduced: false,
    location_id: null,
  });
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const variant = content[lang] ?? emptyVariant();
  const setVariant = (v: CampaignVariant) => setContent({ ...content, [lang]: v });
  const hasNl = !!content.nl;
  const hasEn = !!content.en;

  // Free, but not silent: name the expected tokens a variant leaves out.
  const missing = (['nl', 'en'] as const).flatMap((l) => {
    const v = content[l];
    if (!v) return [];
    const text = variantText(v);
    return mail.expected
      .filter((t) => !hasToken(text, t))
      .map((t) => `${l.toUpperCase()}: {{${t}}}`);
  });

  const save = async (patch?: { enabled?: boolean }) => {
    setBusy('save');
    setError(null);
    setNote(null);
    const res = await saveEmailTemplate(mail.kind, {
      content,
      default_locale: defaultLocale,
      ...patch,
    });
    setBusy(null);
    if (res.ok && res.data) {
      setExists(true);
      setEnabled(res.data.template.enabled);
      setNote(
        res.data.template.enabled
          ? 'Opgeslagen. Deze versie gaat binnen een halve minuut naar klanten.'
          : 'Opgeslagen. Hij staat uit: klanten krijgen nog de standaardmail.',
      );
    } else setError(errText(res.error));
    return res.ok;
  };

  const reset = async () => {
    if (!confirm(`De eigen versie van "${mail.label}" weggooien en terug naar de standaardmail?`)) return;
    setBusy('reset');
    setError(null);
    const res = await resetEmailTemplate(mail.kind);
    setBusy(null);
    if (res.ok) onClose();
    else setError(errText(res.error));
  };

  const test = async () => {
    setBusy('test');
    setError(null);
    setNote(null);
    const res = await testSendEmailTemplate(
      mail.kind,
      showBuiltin ? null : content,
      lang,
      defaultLocale,
      scenario,
      testTo.trim() || undefined,
    );
    setBusy(null);
    if (res.ok && res.data) {
      const who = res.data.sample_names
        ? `${res.data.to} (met voorbeeldnaam — dit adres is geen klant)`
        : res.data.to;
      setNote(
        `Testmail (${res.data.builtin ? 'standaardmail' : 'wat nu in de editor staat'}) naar ${who}.`,
      );
    } else setError(errText(res.error));
  };

  return (
    <div className="marketing-followup">
      <div className="vouchers-toolbar">
        <p className="muted">
          <strong>{mail.label}</strong> — {mail.when}{' '}
          {enabled ? (
            <span className="pill marketing-pill-sent">Eigen versie aan</span>
          ) : (
            <span className="pill marketing-pill-draft">Klanten krijgen de standaardmail</span>
          )}
        </p>
        <div className="vouchers-toolbar-actions">
          <button className="btn-link" onClick={onClose}>← Terug</button>
        </div>
      </div>

      <section className="marketing-panel">
        {!exists ? (
          <p className="muted marketing-hint">
            Deze mail heeft nog geen eigen versie. Hieronder staat een startpunt met de tekst van
            de standaardmail; het gaat pas naar klanten als je het opslaat én aanzet.
          </p>
        ) : null}

        <div className="marketing-lang-tabs">
          {hasNl ? (
            <button
              className={lang === 'nl' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
              onClick={() => setLang('nl')}
            >
              NL{defaultLocale === 'nl' ? ' (standaard)' : ''}
            </button>
          ) : null}
          {hasEn ? (
            <button
              className={lang === 'en' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
              onClick={() => setLang('en')}
            >
              EN{defaultLocale === 'en' ? ' (standaard)' : ''}
            </button>
          ) : null}
          {!hasEn ? (
            <button
              className="btn-link"
              onClick={() => { setContent({ ...content, en: mail.defaults.en ?? emptyVariant() }); setLang('en'); }}
            >
              + Engelse variant
            </button>
          ) : null}
          {!hasNl ? (
            <button
              className="btn-link"
              onClick={() => { setContent({ ...content, nl: mail.defaults.nl ?? emptyVariant() }); setLang('nl'); }}
            >
              + Nederlandse variant
            </button>
          ) : null}
          {hasNl && hasEn ? (
            <button
              className="btn-link voucher-deactivate"
              onClick={() => {
                const next = { ...content };
                delete next[lang];
                const remaining: CampaignLocale = lang === 'nl' ? 'en' : 'nl';
                setContent(next);
                setDefaultLocale(remaining);
                setLang(remaining);
              }}
            >
              {lang === 'nl' ? 'NL-variant verwijderen' : 'EN-variant verwijderen'}
            </button>
          ) : null}
          <button
            className="btn-link"
            onClick={() => {
              if (confirm('De tekst van deze taal vervangen door die van de standaardmail?')) {
                setVariant(mail.defaults[lang] ?? emptyVariant());
              }
            }}
          >
            Tekst van de standaardmail
          </button>
        </div>

        <TokenHelp mail={mail} />

        <div className="marketing-editor-grid">
          <div className="marketing-editor-col">
            <label className="walkin-field">
              <span>Onderwerp ({lang.toUpperCase()})</span>
              <input
                type="text"
                value={variant.subject}
                onChange={(e) => setVariant({ ...variant, subject: e.target.value })}
              />
            </label>
            <label className="walkin-field">
              <span>Preheader (voorbeeldtekst naast het onderwerp, optioneel)</span>
              <input
                type="text"
                value={variant.preheader ?? ''}
                onChange={(e) => setVariant({ ...variant, preheader: e.target.value || null })}
              />
            </label>

            <div className="marketing-mode-toggle">
              <button
                className={variant.mode === 'blocks' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
                onClick={() => setVariant({ ...variant, mode: 'blocks', blocks: variant.blocks ?? emptyVariant().blocks })}
              >
                Blokken
              </button>
              <button
                className={variant.mode === 'html' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
                onClick={() => setVariant({ ...variant, mode: 'html', html: variant.html ?? '' })}
              >
                HTML plakken
              </button>
            </div>

            {variant.mode === 'blocks' ? (
              <BlocksEditor
                blocks={variant.blocks ?? []}
                onChange={(blocks) => setVariant({ ...variant, blocks })}
              />
            ) : (
              <>
                <textarea
                  className="marketing-html-input"
                  rows={16}
                  value={variant.html ?? ''}
                  placeholder="<html>…</html> — plak hier de volledige e-mail-HTML"
                  onChange={(e) => setVariant({ ...variant, html: e.target.value })}
                  spellCheck={false}
                />
                <p className="muted marketing-hint">
                  Je HTML gaat zoals hij is. Alleen de afmeld-voettekst komt eronder, tenzij je zelf{' '}
                  <code>{'{{unsubscribe_url}}'}</code> in de mail zet. Onderdelen als{' '}
                  <code>{'{{qr_code}}'}</code> zet je op de plek waar ze moeten staan.
                </p>
              </>
            )}
          </div>

          <ServiceMailPreview
            kind={mail.kind}
            content={showBuiltin ? null : content}
            lang={lang}
            defaultLocale={defaultLocale}
            scenario={scenario}
          />
        </div>
      </section>

      {missing.length > 0 ? (
        <div className="error-card">
          Let op: {missing.join(', ')} {missing.length === 1 ? 'staat' : 'staan'} niet in deze
          mail. Dat mag, maar dan mist de klant dat onderdeel.
        </div>
      ) : null}
      {error ? <div className="error-card">{error}</div> : null}
      {note ? <p className="muted">{note}</p> : null}

      <div className="walkin-row">
        <label className="walkin-field">
          <span>Voorbeeld van</span>
          <select
            value={showBuiltin ? 'builtin' : 'custom'}
            onChange={(e) => setShowBuiltin(e.target.value === 'builtin')}
          >
            <option value="custom">Wat in de editor staat</option>
            <option value="builtin">De standaardmail</option>
          </select>
        </label>
        <label className="walkin-field">
          <span>Vestiging in het voorbeeld</span>
          <select
            value={scenario.location_id ?? ''}
            onChange={(e) => setScenario({ ...scenario, location_id: e.target.value || null })}
          >
            <option value="">Eerste vestiging met een adres</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.display_name ?? l.name}</option>
            ))}
          </select>
        </label>
        {BOOKING_KINDS.includes(mail.kind) ? (
          <label className="walkin-field">
            <span>
              <input
                type="checkbox"
                checked={scenario.unpaid === true}
                onChange={(e) => setScenario({ ...scenario, unpaid: e.target.checked })}
              />{' '}
              Nog niet betaald
            </span>
          </label>
        ) : null}
        {SPLIT_KINDS.includes(mail.kind) ? (
          <label className="walkin-field">
            <span>
              <input
                type="checkbox"
                checked={scenario.split === true}
                onChange={(e) => setScenario({ ...scenario, split: e.target.checked })}
              />{' '}
              Groep over twee tijdsloten
            </span>
          </label>
        ) : null}
        {BOOKING_KINDS.includes(mail.kind) ? (
          <label className="walkin-field">
            <span>
              <input
                type="checkbox"
                checked={scenario.no_vat === true}
                onChange={(e) => setScenario({ ...scenario, no_vat: e.target.checked })}
              />{' '}
              Zonder BTW
            </span>
          </label>
        ) : null}
        {REFUND_KINDS.includes(mail.kind) ? (
          <label className="walkin-field">
            <span>
              <input
                type="checkbox"
                checked={scenario.reduced === true}
                onChange={(e) => setScenario({ ...scenario, reduced: e.target.checked })}
              />{' '}
              Verkleind in plaats van geannuleerd
            </span>
          </label>
        ) : null}
      </div>

      <div className="walkin-row">
        <label className="walkin-field">
          <span>Testmail naar</span>
          <input
            type="email"
            placeholder="leeg = jezelf"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
          />
        </label>
      </div>

      <div className="marketing-actions">
        <button className="btn-link" disabled={busy != null} onClick={() => void test()}>
          {busy === 'test' ? 'Versturen…' : 'Testmail versturen'}
        </button>
        <button className="btn-link" disabled={busy != null} onClick={() => void save()}>
          {busy === 'save' ? 'Opslaan…' : 'Opslaan'}
        </button>
        <button
          className={enabled ? 'btn-link voucher-deactivate' : 'walkin-primary-btn'}
          disabled={busy != null}
          onClick={() => void save({ enabled: !enabled })}
        >
          {enabled ? 'Uitzetten (terug naar standaardmail)' : 'Opslaan en aanzetten'}
        </button>
        {exists ? (
          <button className="btn-link voucher-deactivate" disabled={busy != null} onClick={() => void reset()}>
            Eigen versie weggooien
          </button>
        ) : null}
      </div>
    </div>
  );
}

function TokenHelp({ mail }: { mail: TransactionalMail }) {
  const values = mail.tokens.filter((t) => !t.section && !t.condition);
  const sections = mail.tokens.filter((t) => t.section);
  const conditions = mail.tokens.filter((t) => t.condition);
  return (
    <details className="marketing-hint">
      <summary className="muted">Tokens voor deze mail</summary>
      <p className="muted marketing-hint">
        <strong>Waarden</strong> — worden tekst:{' '}
        {values.map((t, i) => (
          <span key={t.token} title={t.label}>
            {i > 0 ? ' ' : null}<code>{`{{${t.token}}}`}</code>
          </span>
        ))}
      </p>
      {sections.length > 0 ? (
        <>
          <p className="muted marketing-hint">
            <strong>Onderdelen</strong> — worden een kant-en-klaar stuk mail, precies zoals in de
            standaardmail. Zet het token als enige tekst in een alinea-blok, of in je HTML waar
            het moet komen. Een onderdeel dat niet van toepassing is, valt weg.
          </p>
          <ul className="muted marketing-hint">
            {sections.map((t) => (
              <li key={t.token}><code>{`{{${t.token}}}`}</code> — {t.label}</li>
            ))}
          </ul>
        </>
      ) : null}
      {conditions.length > 0 ? (
        <>
          <p className="muted marketing-hint">
            <strong>Voorwaarden</strong> — <code>{'{{#onbetaald}}'}</code> … <code>{'{{/onbetaald}}'}</code>{' '}
            laat wat ertussen staat alleen zien als het van toepassing is,{' '}
            <code>{'{{^onbetaald}}'}</code> … <code>{'{{/onbetaald}}'}</code> alleen als het niet zo is.
            Binnen één alinea, in je HTML of in het onderwerp. Over meerdere blokken (tekst plus knop):
            zet <code>{'{{#onbetaald}}'}</code> en <code>{'{{/onbetaald}}'}</code> elk als enige tekst in
            een eigen alinea-blok. Elke waarde of elk onderdeel werkt ook als voorwaarde (van toepassing
            als het niet leeg is).
          </p>
          <ul className="muted marketing-hint">
            {conditions.map((t) => (
              <li key={t.token}><code>{`{{#${t.token}}}`}</code> — {t.label}</li>
            ))}
          </ul>
        </>
      ) : null}
      <p className="muted marketing-hint">
        Zonder <code>{'{{unsubscribe_url}}'}</code> in de mail komt de standaard-voettekst eronder
        (waarom de klant de mail krijgt, en afmelden voor nieuws en aanbiedingen). Afmelden stopt
        alleen marketing: bevestigingen en herinneringen komen gewoon.
      </p>
      <p className="muted marketing-hint">
        Een link-token zoals <code>{'{{beheer_url}}'}</code> kan ook in het linkveld van een
        knop-blok. Hoofdletters, spaties en underscores maken niet uit. Afbeeldingen: gebruik
        JPEG of PNG — AVIF en WebP blijven leeg in veel mailclients.
      </p>
    </details>
  );
}

// The SERVER renders the preview on a sample booking, through the same code a
// real send uses; the QR comes in as an inline image because an iframe cannot
// show the cid: attachment a real mail carries.
function ServiceMailPreview({
  kind,
  content,
  lang,
  defaultLocale,
  scenario,
}: {
  kind: TransactionalKind;
  content: CampaignContent | null;
  lang: CampaignLocale;
  defaultLocale: CampaignLocale;
  scenario: SampleScenario;
}) {
  const [html, setHtml] = useState('');
  const [text, setText] = useState('');
  const [subject, setSubject] = useState('');
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [problem, setProblem] = useState<string | null>(null);
  const [tab, setTab] = useState<'render' | 'text' | 'raw'>('render');
  const seq = useRef(0);

  useEffect(() => {
    if (content && !content[lang]?.subject.trim()) {
      setState('idle');
      setHtml('');
      setText('');
      setSubject('');
      return;
    }
    setState('loading');
    const mySeq = ++seq.current;
    const t = setTimeout(async () => {
      const res = await previewEmailTemplate(kind, content, lang, defaultLocale, scenario);
      if (seq.current !== mySeq) return; // verouderd antwoord
      if (res.ok && res.data) {
        setHtml(res.data.html);
        setText(res.data.text);
        setSubject(res.data.subject);
        setProblem(null);
        setState('idle');
      } else {
        setProblem(errText(res.error));
        setState('error');
      }
    }, 600);
    return () => clearTimeout(t);
  }, [kind, content, lang, defaultLocale, scenario]);

  return (
    <div className="marketing-preview">
      <div className="marketing-preview-head">
        <span className="marketing-block-type">
          {content ? `Voorbeeld (${lang.toUpperCase()})` : `Standaardmail (${lang.toUpperCase()})`}
        </span>
        <span className="marketing-block-tools">
          {state === 'loading' ? <span className="muted small">renderen…</span> : null}
          {state === 'error' ? <span className="muted small">{problem ?? 'voorbeeld niet beschikbaar'}</span> : null}
          {(['render', 'text', 'raw'] as const).map((t) => (
            <button
              key={t}
              className={tab === t ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
              onClick={() => setTab(t)}
            >
              {t === 'render' ? 'Weergave' : t === 'text' ? 'Tekstversie' : 'Broncode'}
            </button>
          ))}
        </span>
      </div>
      {subject ? <div className="marketing-preview-subject">{subject}</div> : null}
      {html && tab === 'render' ? <ScaledEmailFrame html={html} /> : null}
      {html && tab === 'text' ? (
        <pre className="marketing-preview-raw marketing-preview-text">{text}</pre>
      ) : null}
      {html && tab === 'raw' ? <pre className="marketing-preview-raw">{html}</pre> : null}
      {!html ? (
        <div className="marketing-preview-empty muted">
          Vul onderwerp en inhoud in om het voorbeeld te zien.
        </div>
      ) : null}
    </div>
  );
}
